import { useEffect, useState } from 'react';

import { useEstimationItemAssumptionQueryActions } from '@/api/hooks/estimationItemAssumptionQueryActions';
import type { UpdateEstimationItemAssumptionRequest } from '@/api/schema/data-contracts';
import { ApiEstimationStage } from '@/api/schema/data-contracts';
import { AssumptionEditationModal } from '@/components/Assumption/AssumptionEditationModal';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import * as estimationItemHelpers from '@/helpers/estimationItemHelpers';
import { useEstimationStage } from '@/hooks/useEstimationStage';
import { useGetEstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';

import { AssumptionCreationModal } from './AssumptionCreationModal';
import { AssumptionDeletionConfirmationModal } from './AssumptionDeletionConfirmationModal';
import { AssumptionsTable } from './AssumptionsTable';
import type {
  AssumptionActionCallback,
  AssumptionModel,
  AssumptionValidityChangeActionCallback,
} from './model';

export interface AssumptionsContainerProps {
  estimationId: string;
  estimationItemId?: string;
  isReadOnlyForced?: boolean;
}

export const AssumptionsContainer = ({
  estimationId,
  estimationItemId,
  isReadOnlyForced,
}: AssumptionsContainerProps) => {
  const { isLoading, data } = useGetEstimationWithDependencies(estimationId);
  const { useUpdateEstimationItemAssumption, useDeleteEstimationItemsAssumption } =
    useEstimationItemAssumptionQueryActions();
  const { mutateAsync: mutateEstimationItemAssumptionAsync } = useUpdateEstimationItemAssumption();
  const { mutateAsync: mutateDeleteEstimationItemAssumptionAsync } =
    useDeleteEstimationItemsAssumption();
  const estimationStage = useEstimationStage(estimationId);
  const hasEstimationEditPermission = useHasEstimationPermission(estimationId, 'CAN_EDIT');
  const [searchText, setSearchText] = useState('');
  const [estimationData, setEstimationData] = useState(data);
  const [assumptionToDelete, setAssumptionToDelete] = useState<string | null>(null);

  useEffect(() => {
    setEstimationData(data);
  }, [data]);

  const isReadOnly =
    isReadOnlyForced === true
      ? true
      : estimationStage === ApiEstimationStage.IN_REVIEW ||
        estimationStage === ApiEstimationStage.APPROVED ||
        !hasEstimationEditPermission;

  const [assumptionIdForEditationModal, setAssumptionIdForEditationModal] = useState<string>();
  const [estimationItemIdForAsumptionCreationModal, setEstimationItemIdForAsumptionCreationModal] =
    useState<string>();

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (data === undefined || estimationData === undefined) {
    return <TableSkeleton />;
  }

  const assumptionModels: AssumptionModel[] = estimationData.assumptions
    .filter((x) => (estimationItemId ? x.estimationItemId === estimationItemId : true))
    .map((assumption) => {
      const estimationItem = estimationData.estimationItems.find(
        (x) => x.id === assumption.estimationItemId,
      )!;

      return {
        id: assumption.id,
        categoryName: assumption.estimationModelAssumptionCategoryId
          ? estimationData.estimationModel.assumptionCategories.find(
              (assumptionCategory) =>
                assumptionCategory.id === assumption.estimationModelAssumptionCategoryId,
            )?.name
          : undefined,
        description: assumption.description,
        estimationItem: {
          name: estimationItem.name,
          reference: estimationItemHelpers.buildHierarchicalOrderReferenceString(
            estimationItem,
            estimationData.estimationItems,
          ),
        },
        isValid: assumption.isValid,
        advisoryAssumptionRef: assumption.advisoryAssumptionRef ?? undefined,
      };
    });

  const onEditClicked: AssumptionActionCallback = (estimationItemAssumptionId) => {
    setAssumptionIdForEditationModal(estimationItemAssumptionId);
  };

  const onDeleteClicked: AssumptionActionCallback = (estimationItemAssumptionId) => {
    setAssumptionToDelete(estimationItemAssumptionId);
  };

  const handleDeleteConfirm = () => {
    if (assumptionToDelete) {
      void mutateDeleteEstimationItemAssumptionAsync(assumptionToDelete);
      setAssumptionToDelete(null);
    }
  };

  const onAssumptionValidityChangedAsync: AssumptionValidityChangeActionCallback = async (
    estimationItemAssumptionId,
    isValid,
  ): Promise<void> => {
    const assumption = estimationData.assumptions.find((x) => x.id === estimationItemAssumptionId);

    // optimistic update - prevent switches flickering in the table
    setEstimationData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        assumptions: prev.assumptions.map((x) =>
          x.id === estimationItemAssumptionId ? { ...x, isValid } : x,
        ),
      };
    });

    const updateAssumptionRequest: UpdateEstimationItemAssumptionRequest = {
      estimationModelAssumptionCategoryId: assumption?.estimationModelAssumptionCategoryId,
      description: assumption?.description ?? '',
      isValid,
    };
    await mutateEstimationItemAssumptionAsync({
      estimationItemAssumptionId,
      estimationItemAssumption: updateAssumptionRequest,
    });
  };

  return (
    <div
      className='dark:bg-c16 overflow-x-auto overflow-y-auto rounded-xl px-2 py-10'
      data-testid='assumptions-container'
    >
      <AssumptionsTable
        estimationId={estimationId}
        estimationItemId={estimationItemId}
        assumptions={assumptionModels}
        onAssumptionEditClicked={onEditClicked}
        onAssumptionDeleteClicked={onDeleteClicked}
        setSearchText={setSearchText}
        onAddAssumptionClicked={
          estimationId
            ? () => {
                setEstimationItemIdForAsumptionCreationModal(estimationItemId);
              }
            : () => {}
        }
        showButtons={true}
        showEstimationItem={!estimationItemId}
        onAssumptionValidityChanged={onAssumptionValidityChangedAsync}
        isReadOnly={isReadOnly}
        searchText={searchText}
      />
      <AssumptionEditationModal
        open={!!assumptionIdForEditationModal}
        onClose={() => setAssumptionIdForEditationModal(undefined)}
        estimationModelId={estimationData.estimationModel.id}
        estimationItemAssumptionId={assumptionIdForEditationModal}
        estimationCultureName={data.estimation.cultureName}
      />
      {estimationItemId && (
        <AssumptionCreationModal
          open={!!estimationItemIdForAsumptionCreationModal}
          onClose={() => setEstimationItemIdForAsumptionCreationModal(undefined)}
          estimationItemId={estimationItemId}
          estimationModelId={estimationData.estimationModel.id}
          estimationCultureName={data.estimation.cultureName}
        />
      )}
      <AssumptionDeletionConfirmationModal
        open={!!assumptionToDelete}
        onClose={() => setAssumptionToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
